import { FormControlLabel, Typography } from '../../UIKit';
import React, { ChangeEvent, useState, useEffect } from 'react';
import { SearchTextField, PopoverToModal } from '~/Components';
import { BORDER_COLOR, LIGHT_BLUE, NOTE_RED } from '../../Constants/Colors.constants';
import { useHasAccess, useLanguage, useNavigation } from '../../Hooks';
import { ILocation } from '../../Models/DTO/Location.models';
import { Link, List, Radio, RadioGroup } from '@mui/material';
import { MapIcon } from '../../Components/Icons';
import ListItemSelect from '../../Screens/Events/EventPage/Components/ListItemSelect';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { selectLocationById, useLocations } from '../../store/provider';
import { requestDismissWarning, selectIsWarningDismissed } from '../../store/user';
import { makeStyles } from '~/UIKit/Style';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { useRequestLocationsChannels } from '~/requests';
import { EventType } from '~/Models/DTO/Event.model';

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  updateLocation: (location: ILocation) => void;
  selectedLocationId: string;
  anchorEl?: null | Element;
}

const useStyles = makeStyles(() => ({
  content: {
    padding: '0 15px',
  },
  infoText: {
    margin: '5px 0',
    fontSize: 12,
    lineHeight: '16px',
  },
  note: {
    background: LIGHT_BLUE,
    borderTop: `1px solid ${BORDER_COLOR}`,
    borderBottom: `1px solid ${BORDER_COLOR}`,
    margin: '0 -15px',
    padding: 15,
  },
  noteHighlighted: {
    color: NOTE_RED,
  },
  searchContainer: {
    margin: '0 -15px',
    position: 'sticky',
    top: 0,
    zIndex: 1, // To prevent the list from scrolling over the search bar
  },
  locationList: {
    borderTop: `1px solid ${BORDER_COLOR}`,
    margin: '0 -15px',
    padding: 0,
  },
  radioGroup: {
    marginLeft: 'auto',
  },
  radio: {
    marginRight: 0,
  },
}));

export const useLocationSelectionModal = ({
  updateLocationProp,
  selectedLocationId,
  onClose,
  open,
}: {
  updateLocationProp: (location: ILocation) => void;
  selectedLocationId: string;
  onClose: () => void;
  open: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();
  const { hasAccess } = useHasAccess();
  const { locationList } = useLocations();
  const { getParams } = useNavigation();
  const { eventType } = getParams<{ eventType: EventType }>();

  const isFreeTraining = eventType === EventType.FREE_TRAINING;

  const locationsChannelsQuery = useRequestLocationsChannels(
    (locationList ?? [])?.map((l) => l.id),
    !isFreeTraining,
  );

  const dismissUSCWarning = () =>
    bindActionCreators(requestDismissWarning, dispatch)('uscLocationChangeWarning');

  const showUSCWarning =
    !useSelector(selectIsWarningDismissed('uscLocationChangeWarning')) &&
    !hasAccess(AccessList.HIDE_USC_LOCATION_WARNING_PARTNER_TOOL);
  const currentLocation = useSelector(selectLocationById(selectedLocationId));

  const filteredLocations = locationList.filter((location) =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const [location, setLocation] = useState<ILocation>(currentLocation);

  const updateLocation = () => {
    updateLocationProp(location);
  };

  const handleActionTaken = () => {
    updateLocation();
    onClose();
  };

  useEffect(() => {
    if (open) {
      setSearchQuery('');
    }
  }, [open]);

  return {
    showUSCWarning,
    dismissUSCWarning,
    currentLocation,
    locations: filteredLocations,
    locationsChannels: locationsChannelsQuery.data,
    doSearch: setSearchQuery,
    updateLocation,
    setLocation,
    location,
    handleActionTaken,
    eventType,
  };
};

export const LocationSelectionModal = ({
  open,
  onClose,
  selectedLocationId,
  updateLocation: updateLocationProp,
  anchorEl,
}: LocationModalProps) => {
  const {
    doSearch,
    locations,
    showUSCWarning,
    dismissUSCWarning,
    handleActionTaken,
    setLocation,
    location,
    locationsChannels,
    eventType,
  } = useLocationSelectionModal({ updateLocationProp, selectedLocationId, onClose, open });

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    doSearch(event.currentTarget.value);
  };

  const { classes } = useStyles();
  const { t } = useLanguage();

  return (
    <PopoverToModal
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      title={t('app.event.changeLocation')}
      actionName={t('app.checkin.select.done')}
      onActionTaken={handleActionTaken}
    >
      <div className={classes.content}>
        {showUSCWarning && (
          <div className={classes.note}>
            <Typography className={classes.infoText}>
              <strong className={classes.noteHighlighted}>{t('app.note')}&nbsp;</strong>
              {t('app.event.changeLocation.uscWarning')}
            </Typography>
            <Typography className={classes.infoText}>
              <Link onClick={dismissUSCWarning} underline="hover">
                {t('app.doNotShowThisAgain')}
              </Link>
            </Typography>
          </div>
        )}
        <div className={classes.searchContainer}>
          <SearchTextField
            id="searchLocations"
            onChange={handleSearch}
            placeholder={t('app.event.changeLocation.searchForNewLocation')}
            withBackground
          />
        </div>
        <List className={classes.locationList} data-testid="ChangeLocationList">
          {/*
            Checking that we've fetched the location channels before rendering the locations list
            ensures locations with active integrations don't show up as enabled initially.
            We only perform this check for non-free training events.
          */}
          {(eventType === EventType.FREE_TRAINING || locationsChannels) &&
            locations.map((l) => (
              <ListItemSelect
                key={l.uuid}
                onClick={() => setLocation(l)}
                icon={<MapIcon />}
                name={l.name}
                testId={'ChangeLocationListItem'}
                disabled={
                  // Disable locations with active integrations for all event types except free training
                  eventType !== EventType.FREE_TRAINING &&
                  locationsChannels?.[l.id]?.some((c) => c.status === 'active')
                }
              >
                <RadioGroup
                  aria-label="location"
                  name="location"
                  value={selectedLocationId}
                  onChange={() => setLocation(l)}
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    value={l.uuid}
                    control={<Radio />}
                    label=""
                    className={classes.radio}
                    checked={location?.uuid === l.uuid}
                  />
                </RadioGroup>
              </ListItemSelect>
            ))}
        </List>
      </div>
    </PopoverToModal>
  );
};

export default LocationSelectionModal;
